'use client';

import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@/components/atoms/Table';
import { Pagination } from '@/components/molecules/Pagination';
import { CartToggleButton } from '@/features/cart/components/atoms/CartToggleButton';
import { useCartStore } from '@/features/cart/stores/cartStore';
import { ChatOverlay } from '@/features/chat/components/organisms/ChatOverlay';
import { useChatStore } from '@/features/chat/stores/chatStore';
import { trackingLogService } from '@/features/tracking-log/trackingLogService';
import { Button } from '@/shared/components/atoms/Button';
import { useIndicator } from '@/shared/hooks/useIndicator';
import { trackEvent } from '@/shared/utils/mixpanel';
import logger from '@/utils/logger';
import { scrollToTop } from '@/utils/util';
import { CheckedState } from '@radix-ui/react-checkbox';
import { useSession } from 'next-auth/react';
import Image from 'next/image';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import StorageTag from '../../../../shared/components/molecules/StorageTag';
import TruncatedCell from '../../../../shared/components/molecules/TruncatedCell';
import { useAllFilterStore } from '../../stores/AllFilterStore';
import { useFilterStore } from '../../stores/filterStore';
import { useProductStore } from '../../stores/productStore';
import { AllFiltersPanel } from '../molecules/AllFiltersPanel';
import { SellerChatModal } from '../molecules/SellerChatModal';
import { ProductTableHeader } from './ProductTableHeader';

export default function ProductTable() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { showLoading, hideLoading } = useIndicator();
  const [selectedProductForChat, setSelectedProductForChat] = useState<number | null>(null);

  const {
    products,
    currentPage,
    totalPages,
    isLoading,
    error,
    fetchProducts,
    fetchSearchProducts,
    pageChange,
    ordering,
    toggleOrdering,
    toggleCart,
  } = useProductStore();

  const {
    filters,
    tempFilters,
    applyTempFilters,
    handleChangeCheckedTempFilter,
    resetTempFilter,
    resetTempFilters,
    restoreOriginalFilters,
  } = useFilterStore();

  const { showAllFilters, setShowAllFilters } = useAllFilterStore();
  const { addToCart, removeFromCartByProductId } = useCartStore();
  const { isOverlayOpen, toggleOverlay } = useChatStore();

  const { data: session } = useSession();
  const isAuthenticated = !!session?.user?.accessToken;

  useEffect(() => {
    trackingLogService.saveUtmParams();
    trackEvent('Page View', { 'Page Name': '상품 목록' });

    document.body.classList.add('product-table-view');

    return () => {
      document.body.classList.remove('product-table-view');
    };
  }, []);

  useEffect(() => {
    if (isLoading) {
      showLoading();
    } else {
      hideLoading();
    }
  }, [isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // URL에서 검색어 확인
    const searchTerm = searchParams.get('search');

    if (searchTerm) {
      // 검색어가 있으면 검색 실행
      fetchSearchProducts(searchTerm);
    } else {
      // 검색어가 없으면 일반 제품 목록 가져오기
      fetchProducts();
    }
  }, [fetchProducts, fetchSearchProducts, searchParams]);

  const handleItemSelect = (productId: number, checked: CheckedState) => {
    logger.info('handleItemSelect', productId, checked);
    if (isAuthenticated) {
      toggleCart(productId);
      if (checked) {
        addToCart(productId);
      } else {
        removeFromCartByProductId(productId);
      }
    } else {
      router.push('/signin');
    }
  };

  const handlePageChange = (page: number) => {
    pageChange(page);
    scrollToTop();
  };

  const handleLoginClick = () => {
    router.push('/signin');
  };

  const renderSkeletonRows = () => {
    const rows = 16;

    return Array(10)
      .fill(0)
      .map((_, index) => (
        <TableRow key={`skeleton-${index}`} className="border-b animate-pulse">
          {Array.from({ length: rows }).map((_, colIndex) => (
            <TableCell
              key={`skeleton-${index}-col-${colIndex}`}
              className={colIndex === 9 ? 'w-36 h-10' : 'h-10'}
            >
              <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto" />
            </TableCell>
          ))}
        </TableRow>
      ));
  };

  if (error) {
    return <div className="p-4">Error: {error.message}</div>; // TODO 에러 페이지 추가
  }

  return (
    <>
      <div className="w-full mt-5 mb-16">
        {showAllFilters && (
          <div className="w-full">
            <AllFiltersPanel
              tempFilters={tempFilters}
              onClose={() => {
                restoreOriginalFilters();
                setShowAllFilters(false);
              }}
              onChangeCheckedTempFilter={handleChangeCheckedTempFilter}
              onClearTempFilters={() => {
                resetTempFilters();
              }}
              onApplyTempFilters={() => {
                applyTempFilters();
                setShowAllFilters(false);
              }}
            />
          </div>
        )}

        {!showAllFilters && (
          <div className="w-full">
            <div className="w-full max-w-[1800px] overflow-x-auto shadow-lg">
              <Table className="w-full">
                <ProductTableHeader
                  filters={filters}
                  tempFilters={tempFilters}
                  ordering={ordering}
                  toggleOrdering={toggleOrdering}
                  resetTempFilter={resetTempFilter}
                  applyTempFilters={applyTempFilters}
                  handleChangeCheckedTempFilter={handleChangeCheckedTempFilter}
                  restoreOriginalFilters={restoreOriginalFilters}
                  isAuthenticated={isAuthenticated}
                />
                <TableBody>
                  {isLoading
                    ? renderSkeletonRows()
                    : products.map((product) => (
                      <TableRow
                        key={product.id}
                        aria-selected={product.is_in_cart}
                      >
                        <TableCell>
                          <CartToggleButton
                            className="mx-auto"
                            checked={product.is_in_cart}
                            onCheckedChange={(checked: CheckedState) =>
                              handleItemSelect(product.id ?? -1, checked)
                            }
                          />
                        </TableCell>
                        <TableCell>{product.meatType}</TableCell>
                        <TableCell>{product.origin}</TableCell>
                        <TableCell className="text-center">
                          <StorageTag type={product.storageType} />
                        </TableCell>
                        <TableCell>{product.cut}</TableCell>
                        <TableCell>{product.brand}</TableCell>
                        <TableCell className="text-center">{product.grade}</TableCell>
                        <TableCell className="text-center">
                          {product.est}
                        </TableCell>
                        <TableCell className="text-right">
                          {product.averageWeight?.formatted}
                        </TableCell>
                        <TableCell>
                          <TruncatedCell content={product.description} />
                        </TableCell>
                        <TableCell className="text-right">
                          {product.stock?.formatted}
                        </TableCell>
                        <TableCell>{product.warehouse}</TableCell>
                        <TableCell className="text-center">
                          {product.expiryDate}
                        </TableCell>
                        <TableCell className={`${isAuthenticated ? 'text-right' : 'text-center'}`}>
                          {isAuthenticated ? (
                            <span>{product.price?.formatted}</span>
                          ) : (
                            <Button
                              onClick={handleLoginClick}
                              variant="ghost"
                              className="body-14r text-[#93A0B4] p-0"
                            >
                              로그인 필요
                            </Button>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {product.modifiedDays?.formatted}
                        </TableCell>
                        <TableCell className={'text-[#6D717D]'}>
                          {isAuthenticated ? (
                            product.seller
                          ) : (
                            <Button
                              onClick={handleLoginClick}
                              variant="ghost"
                              className="body-14r text-[#93A0B4] p-0"
                            >
                              로그인 필요
                            </Button>
                          )}
                        </TableCell>
                        {isAuthenticated && (
                          product.sellers && product.sellers.length > 0 && !product.sellers.some(seller => seller.email === session?.user?.email) ? (
                            <TableCell className="text-center">
                              <SellerChatModal
                                isOpen={selectedProductForChat === product.id}
                                isCartProduct={false}
                                onOpenChange={(open) =>
                                  setSelectedProductForChat(open ? product.id || null : null)
                                }
                                product={product}
                                isShowPhoneNumber={false}
                              >
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="p-1 h-10 w-10"
                                  onClick={() => setSelectedProductForChat(product.id || null)}
                                >
                                  <Image
                                    src="/images/email.svg"
                                    alt="message"
                                    width={32}
                                    height={32}
                                  />
                                </Button>
                              </SellerChatModal>
                            </TableCell>
                          ) : (
                            <TableCell className="text-center">
                              <span className="text-[#6D717D] text-sm">-</span>
                            </TableCell>
                          )
                        )}
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>

            <Pagination
              className="mt-4.5"
              currentPage={currentPage}
              setCurrentPage={handlePageChange}
              totalPages={totalPages}
            />
          </div>
        )}
      </div>

      {/* Chat Overlay */}
      <ChatOverlay
        isOpen={isOverlayOpen}
        onClose={toggleOverlay}
      />
    </>
  );
}
